/**
 * projects/v1 wire types — hand-rolled from `contracts/projects/v1/openapi.yaml`.
 *
 * SYNC: projects/v1@2.12.0
 *
 * SB3-CC-BC-projects (projects/v1@2.0.0): `FINISHED → COMPLETED` per
 * `.vibe/03_EVAL_STATE_MACHINE.md` §2.1 — terminal-success rename to
 * align with Contract / ContractPosition / InspectionInstruction /
 * DataAcquisition (all use `COMPLETED`).
 *
 * TNCY-4 (projects/v1@2.4.0) — cross-tenant ownership tell on each
 * `ProjectListItem` (`ownership` / `ownerTenantName` / `role`), a new
 * `reportingSubcontractorTenantId` party field (XT-D22), and a `scope`
 * query param (`own` | `member`) on `GET /projects` that the FE fires
 * twice and merges client-side (TNCY-6, `useOwnerMemberList`). This file
 * is hand-rolled (projects/v1 is NOT codegen'd in this repo — there is no
 * `api.projects.gen.ts`), so it is updated by hand per
 * `project_fe_codegen_drift`.
 *
 * `ProjectState` and the sort enums are kept as closed `as const`
 * arrays so the FE can iterate / switch without a stringly-typed
 * fallthrough.
 *
 * SYNC: projects/v1@2.26.0 for the PM-WF-01 / M3C-03 workflow fields (checked
 * against the generated `api.projects.gen.ts` at the bottom of this file).
 */
import type { ProjectsComponents } from "@aeriosiq/types";

export const PROJECT_STATES = [
  "OFFER",
  "DATA_IMPORT",
  "DATA_VALIDATION_CUSTOMER",
  "PREPARATION",
  "EXECUTION",
  "ACCOUNTING",
  "PAYMENT",
  "COMPLETED",
  "WITHDRAWN",
  "REJECTED",
] as const;
export type ProjectState = (typeof PROJECT_STATES)[number];

export const PROJECTS_SORT_KEYS = [
  "projectCode",
  "projectName",
  "startDate",
  "customerName",
  "contractorName",
  "totalAssets",
  "progressPct",
  "state",
] as const;
export type ProjectsSortKey = (typeof PROJECTS_SORT_KEYS)[number];

export type ProjectsSortDir = "asc" | "desc";

/** TNCY-4 (2.4.0): which side of the cross-tenant fence a project row is on.
 *  `owner` = the caller's own project (default `scope=own`); `member` = a
 *  project the caller was granted cross-tenant read access to (`scope=member`). */
export type ProjectOwnership = "owner" | "member";

/** TNCY-4 (2.4.0): read/list scope. `own` (default) = caller's own projects;
 *  `member` = the cross-tenant fan-out of granted projects. The FE fires both
 *  and merges them into one list (`useOwnerMemberList`). */
export type ProjectsScope = "own" | "member";

/** TNCY-4 / XT-D22 — the caller's access role on a `member` project. Mirrors
 *  the `tenancy/v1` AccessRole enum; null on `owner` rows. */
export const PROJECT_ACCESS_ROLES = [
  "project_customer",
  "project_contractor",
  "contract_customer",
  "contract_contractor",
  "reporting_subcontractor",
  "ii_subcontractor",
] as const;
export type ProjectAccessRole = (typeof PROJECT_ACCESS_ROLES)[number];

/** XT-D21/D22 — customer roles see the outcome surface only (no chain,
 *  no contracts). Used to switch the project list/detail to the customer
 *  read projection (TNCY-6 §d). */
export const CUSTOMER_ACCESS_ROLES: readonly ProjectAccessRole[] = [
  "project_customer",
  "contract_customer",
];

export function isCustomerRole(
  role: ProjectAccessRole | null | undefined,
): boolean {
  return role != null && CUSTOMER_ACCESS_ROLES.includes(role);
}

export interface ProjectListItem {
  projectId: string;
  projectCode: number;
  projectName: string;
  startDate: string | null;
  customerTenantId: string | null;
  customerTenantName: string | null;
  contractorTenantId: string | null;
  contractorTenantName: string | null;
  /** TNCY-1 follow-up (2.4.0): the project's single reporting-subcontractor
   *  party tenant. Stripped from customer-role projections (XT-D22 gap D). */
  reportingSubcontractorTenantId: string | null;
  totalAssets: number;
  progressPct: number;
  state: ProjectState;
  /** TNCY-4 (2.4.0): `owner` | `member`. Optional for back-compat with a
   *  pre-2.4.0 BE; always present from 2.4.0. Absent ⇒ treated as `owner`. */
  ownership?: ProjectOwnership;
  /** TNCY-4: the data-owning tenant's display name on a `member` row; null on
   *  `owner` rows. */
  ownerTenantName?: string | null;
  /** TNCY-4: the caller's access role on a `member` project (XT-D22);
   *  null on `owner` rows. Highest-surface role when several are held. */
  role?: ProjectAccessRole | null;
  /** DL-01 (projects/v1 2.27.0) — see {@link ProjectDetail.customerImageDownload}. */
  customerImageDownload?: ProjectsComponents["schemas"]["ProjectListItem"]["customerImageDownload"];
  /** PM-WF-01 (2.25.0): the project's workflow (a registry key). `null` =
   *  NOT SET — the pilot picks one at intake (DI-D9). On member rows the
   *  owner's value (contractor roles) or `null` (customer roles). */
  workflow: string | null;
  /** PM-WF-01 (2.25.0): the registry label of `workflow`; `null` when unset
   *  or not a registry key (show the key then). */
  workflowLabel: string | null;
  /** M3C-03 (2.26.0): the workflow VERSION the project pins (DI-D61); `null`
   *  for a project created before its tenant owned a workflow. The name is
   *  read from workflows/v1 (M3C-04), never from `workflowLabel`. */
  workflowVersionId: string | null;
}

export interface ProjectListResponse {
  items: ProjectListItem[];
  total: number;
  page: number;
  pageSize: number;
  /** PM-WF-01 (2.25.0): the "Workflow not set" facet count over every other
   *  filter (own scope only; equals `total` under `workflow=unset`). */
  workflowUnsetCount?: number;
}

/** PM-WF-01 — the list filter value for projects without a workflow. */
export const WORKFLOW_UNSET = "unset" as const;

/** PM-WF-01 — `PATCH /projects/{projectId}/workflow` (2.25.0); M3C-03
 *  (2.26.0): one of the two — the FE sends `workflowVersionId` (M3C-04). */
export interface UpdateProjectWorkflowRequest {
  workflow?: string;
  workflowVersionId?: string;
}

/** PM-WF-01 — the PATCH's 200. `affects.newAcquisitionsOnly` is always
 *  `true`: an acquisition copies the workflow at create and keeps it. */
export interface UpdateProjectWorkflowResponse {
  project: ProjectDetail;
  affects: { newAcquisitionsOnly: true };
}

export interface ProjectsFacetOption {
  id: string;
  name: string;
}

export interface ProjectsFacetResponse {
  items: ProjectsFacetOption[];
}

/** PM-1 / PM-4 (projects/v1@2.7.0) — a single `{name, id}` external-id
 *  pair on the project base (`projects.external_ids`, PM-D8). */
export interface ProjectExternalId {
  name: string;
  id: string;
}

/**
 * PM-1 / PM-4 — `POST /projects` request body (`createProject`,
 * projects/v1@2.7.0 `CreateProjectRequest`). `projectName` is the only
 * required field; `projectCode` / `projectId` / audit columns are
 * BE-managed and `state` defaults to `OFFER`.
 *
 * Party `*_tenant_id` fields reuse the TNCY-7 auto-grant seam: naming a
 * party tenant auto-creates the matching `project_role_access_map` grant
 * (XT-D23, owner-`tenant_admin`-gated, PM-D23); the tenant must already
 * be a known, non-suspended link of the owner (else 403, atomic
 * rollback). A `*_contact_id` is descriptive only and never grants.
 */
export interface CreateProjectRequest {
  projectName: string;
  /** PM-WF-01 (2.25.0) / M3C-03 (2.26.0) — one of `workflow` /
   *  `workflowVersionId`. The create wizard sends the version picked at
   *  step 0 (M3C-04); the import commit's `project` object still sends the
   *  legacy key. */
  workflow?: string;
  /** M3C-03 (2.26.0) — an own ACTIVE version (workflows/v1). */
  workflowVersionId?: string;
  state?: ProjectState;
  customerTenantId?: string | null;
  customerContactId?: string | null;
  contractorTenantId?: string | null;
  contractorContactId?: string | null;
  /** PM-D21 — the single reporting-subcontractor party tenant
   *  (auto-grants `reporting_subcontractor`). No contact pair. */
  reportingSubcontractorTenantId?: string | null;
  billingContactId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  offerReference?: string | null;
  reportLanguage?: string | null;
  /** PM-D38 (projects/v1@2.10.0) — per-project AI-model selection as a flat
   *  list of taxonomy AI-model ids (`ai_finding_models.modelId`). The
   *  defect/segmentation `role` split was dropped (a model's `modelType` is a
   *  taxonomy property, not a user bucket). When present the BE derives the
   *  deprecated booleans from `aiModelIds.length > 0` and ignores any
   *  client-sent boolean values. Omit when empty. */
  aiModelIds?: string[];
  /** @deprecated projects/v1@2.8.0 (PM-D33) — use {@link aiModelIds}. Derived
   *  server-side (`aiModelIds.length > 0`); kept for read back-compat. The FE
   *  no longer sends this. */
  aiFindingRequired?: boolean;
  /** @deprecated projects/v1@2.8.0 (PM-D33) — use {@link aiModelIds}. Derived
   *  server-side (`aiModelIds.length > 0`); kept for read back-compat. The FE
   *  no longer sends this. */
  aiComponentSegmentationRequired?: boolean;
  externalIds?: ProjectExternalId[];
}

/**
 * PM-1 / PM-4 — `ProjectDetail` (201 from `createProject`). The persisted
 * project base; extends `ProjectListItem` with the party contact ids and
 * base fields. Only the fields PM-4 consumes are spelled out (the rest are
 * BE-managed audit columns the create page does not read).
 */
export interface ProjectDetail {
  projectId: string;
  projectCode: number;
  projectName: string;
  state: ProjectState;
  customerTenantId: string | null;
  customerContactId: string | null;
  contractorTenantId: string | null;
  contractorContactId: string | null;
  reportingSubcontractorTenantId: string | null;
  startDate: string | null;
  endDate: string | null;
  /** PMGT (2.12.0) — base fields read back for the project-edit Basics step
   *  prefill. `offerReference` / `reportLanguage` are present from create;
   *  `description` has no read-back in `ProjectDetail` (write-only via PATCH),
   *  so it is intentionally absent here. */
  offerReference?: string | null;
  reportLanguage?: string | null;
  externalIds: ProjectExternalId[];
  /** PM-D38 (projects/v1@2.10.0) — per-project AI-model selection read back
   *  from `project_ai_models` as a flat list of taxonomy AI-model ids. Empty
   *  array when none selected. Optional for back-compat with a pre-2.10.0 BE. */
  aiModelIds?: string[];
  /** PM-WF-01 (2.25.0) — see {@link ProjectListItem.workflow}. */
  workflow: string | null;
  workflowLabel: string | null;
  /** M3C-03 (2.26.0) — see {@link ProjectListItem.workflowVersionId}. */
  workflowVersionId: string | null;
  /**
   * DL-01 decision 2 (projects/v1 2.27.0) — `projects.customer_image_download`
   * (default false): the owner lets the customer tenant download
   * full-resolution images. Required on the wire; optional in this
   * hand-rolled mirror because the ~40 project fixtures across the intake
   * and projects tests predate 2.27.0 (absent ⇒ off). The field's TYPE is
   * checked against the generated one below.
   */
  customerImageDownload?: ProjectsComponents["schemas"]["ProjectDetail"]["customerImageDownload"];
  createdAt: string;
  updatedAt: string;
}

/**
 * FB-14 (projects/v1@2.11.0) — one turbine on a project, for the project-detail
 * map (`GET /projects/{projectId}/turbines` → `ProjectTurbineLocations`). The
 * turbine set is the project's DISTINCT `contract_positions.asset_id` (one entry
 * per asset even when the asset sits in several positions). `latitude`/
 * `longitude` are the asset's coordinate, falling back to its site's coordinate
 * when the asset has none; null when neither has coords (skip on the map).
 */
export interface ProjectTurbine {
  assetId: string;
  identifier: string | null;
  internalParkNumber: string | null;
  siteId: string | null;
  siteName: string | null;
  latitude: number | null;
  longitude: number | null;
}

/** FB-14 — `GET /projects/{projectId}/turbines` response. Unpaginated, hard
 *  cap 2000 (the map needs them all; a large import must not clip). */
export interface ProjectTurbineLocations {
  items: ProjectTurbine[];
}

/**
 * PMGT (projects/v1@2.12.0) — `PATCH /projects/{projectId}` request body
 * (`updateProjectParties`, base-field group). Every field is OPTIONAL: an
 * absent key leaves that column unchanged; a present key (including explicit
 * `null` for the nullable fields) SETS it. `projectName` is NOT nullable (the
 * column is NOT NULL). The party `*_tenant_id` fields (XT-D23 auto-grant) stay
 * on the existing `UpdateProjectPartiesRequest` shape — this base-field group
 * is what the project-edit Basics step writes. At least one field must be
 * present. The 200 returns a `ProjectListItem` (re-fetch detail via
 * `GET /projects/{id}` for the full base).
 */
export interface UpdateProjectRequest {
  projectName?: string;
  startDate?: string | null;
  endDate?: string | null;
  offerReference?: string | null;
  reportLanguage?: string | null;
  description?: string | null;
  /** XT-D23 party tenants on the same `PATCH /projects/{id}`
   *  (`updateProjectParties`, projects/v1@2.18.0). Naming a tenant auto-grants
   *  the matching role; `null` clears the field + soft-revokes the auto grant.
   *  The owner-`tenant_admin` gate is enforced on the BE (PM-D23); the FE only
   *  surfaces these for tenant admins (PEDIT-PARTIES). */
  customerTenantId?: string | null;
  customerContactId?: string | null;
  contractorTenantId?: string | null;
  contractorContactId?: string | null;
  /** PM-D21 — the single reporting-subcontractor party tenant (auto-grants
   *  `reporting_subcontractor`). No contact pair. */
  reportingSubcontractorTenantId?: string | null;
}

/**
 * PMGT (projects/v1@2.12.0) — the stable machine-readable conflict reason on a
 * `409` from the two new DELETE endpoints (`ApiError409.reason`). The FE
 * switches over it to surface a friendly inline message.
 *   - `inspection_instruction_linked` — `DELETE /contract-positions/{id}` was
 *     refused because an active inspection instruction references the position.
 *   - `contract_has_positions` — `DELETE /contracts/{id}` was refused because
 *     the contract still has active contract positions (turbines).
 */
export type ProjectsConflictReason =
  | "inspection_instruction_linked"
  | "contract_has_positions";

export interface ListProjectsParams {
  q?: string;
  customerIds?: readonly string[];
  contractorIds?: readonly string[];
  /** SB4-05 / F-05 — explicit state multi-select. When non-empty, the
   *  BE narrows to these states ONLY (takes precedence over
   *  `includeCompleted`). Empty / absent → BE applies the default
   *  L1 behaviour driven by `includeCompleted`. */
  states?: readonly ProjectState[];
  includeCompleted?: boolean;
  sortBy?: ProjectsSortKey;
  sortDir?: ProjectsSortDir;
  page?: number;
  pageSize?: number;
  /** TNCY-4 (2.4.0): `own` (default, single-schema fast path) | `member`
   *  (cross-tenant fan-out of granted projects). Omitted ⇒ BE defaults to
   *  `own`. The FE fires both and merges (`useOwnerMemberList`). */
  scope?: ProjectsScope;
  /** PM-WF-01 (2.25.0): `unset` → only projects without a workflow; a
   *  registry key → only projects with it. Own scope only (the BE ignores it
   *  on `scope=member`, and the merged list drops member rows while it is
   *  set — they are not the caller's to set). */
  workflow?: string;
}

// ─── PM-WF-01 — the hand-rolled fields stay equal to the pinned contract ───
// The projects feature keeps its hand-rolled types (SB2-W-16); these
// assignments fail the typecheck if the new fields and the generated
// projects/v1 2.25.0 types ever disagree (both directions).
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
type GenSchemas = ProjectsComponents["schemas"];
export const PROJECT_WORKFLOW_FIELDS_MATCH_CONTRACT: [
  Same<ProjectListItem["workflow"], GenSchemas["ProjectListItem"]["workflow"]>,
  Same<
    ProjectListItem["workflowLabel"],
    GenSchemas["ProjectListItem"]["workflowLabel"]
  >,
  Same<ProjectDetail["workflow"], GenSchemas["ProjectDetail"]["workflow"]>,
  Same<
    CreateProjectRequest["workflow"],
    GenSchemas["CreateProjectRequest"]["workflow"]
  >,
  Same<
    UpdateProjectWorkflowRequest,
    GenSchemas["UpdateProjectWorkflowRequest"]
  >,
  Same<
    UpdateProjectWorkflowResponse["affects"],
    GenSchemas["ProjectWorkflowAffects"]
  >,
  Same<
    ProjectListItem["workflowVersionId"],
    GenSchemas["ProjectListItem"]["workflowVersionId"]
  >,
  // DL-01 (2.27.0) — the flag's type (optional here, see the field's note)
  Same<
    Required<ProjectDetail>["customerImageDownload"],
    GenSchemas["ProjectDetail"]["customerImageDownload"]
  >,
  Same<
    Required<ProjectListItem>["customerImageDownload"],
    GenSchemas["ProjectListItem"]["customerImageDownload"]
  >,
  Same<
    ProjectDetail["workflowVersionId"],
    GenSchemas["ProjectDetail"]["workflowVersionId"]
  >,
  Same<
    CreateProjectRequest["workflowVersionId"],
    GenSchemas["CreateProjectRequest"]["workflowVersionId"]
  >,
] = [true, true, true, true, true, true, true, true, true, true, true];
